import { FormatNumber } from '@chakra-ui/react';
import type { ReactNode } from 'react';
import type { MentionBoostInsightsDto, MentionBoostInsightsView } from '@fluenceur/types';
import { useTranslation } from 'react-i18next';
import { DataListItem, DataListRoot } from '@/components/ui/data-list';
import { formatCount, formatPercent, formatRatio } from './boost-insights-format';

interface BoostInsightsMetricGridProps {
  insights: MentionBoostInsightsDto;
  view: MentionBoostInsightsView;
  locale: string;
}

interface MetricRow {
  key: string;
  label: string;
  value: ReactNode;
}

const PLACEHOLDER = '--';

/**
 * The fuller metric list under the headline summary: impressions/reach, CPM,
 * frequency, the traffic pair (CTR/CPC), and the conversion pair (CVR/AOV) only
 * when the conversion view is in play. Presented as a simple horizontal DataList
 * with separators (label left, value right), so it reads as a quiet detail list
 * under the headline pills. Currency formats via `FormatNumber`; nulls render a
 * neutral placeholder, never a fake zero.
 */
export function BoostInsightsMetricGrid({ insights, view, locale }: BoostInsightsMetricGridProps) {
  const { t } = useTranslation();
  const { currencyCode } = insights;
  const tk = (key: string) => t(`mentions.boost.insights.metrics.${key}`);
  const money = (value: number | null): ReactNode =>
    value === null ? (
      PLACEHOLDER
    ) : (
      <FormatNumber value={value} style="currency" currency={currencyCode} />
    );

  const rows: MetricRow[] = [
    {
      key: 'impressions',
      label: tk('impressions'),
      value: formatCount(insights.impressions, locale),
    },
    { key: 'reach', label: tk('reach'), value: formatCount(insights.reach, locale) },
    { key: 'cpm', label: tk('cpm'), value: money(insights.cpm) },
    { key: 'frequency', label: tk('frequency'), value: formatRatio(insights.frequency, locale) },
    { key: 'ctr', label: tk('ctr'), value: formatPercent(insights.ctr, locale) },
    { key: 'cpc', label: tk('cpc'), value: money(insights.cpc) },
  ];

  if (view === 'conversion') {
    rows.push(
      { key: 'cvr', label: tk('cvr'), value: formatPercent(insights.cvr, locale) },
      { key: 'aov', label: tk('aov'), value: money(insights.aov) }
    );
  }

  return (
    <DataListRoot orientation="horizontal" divideY="1px" data-testid="boost-insights-metric-grid">
      {rows.map((row) => (
        <DataListItem
          key={row.key}
          pt={2.5}
          label={row.label}
          value={row.value}
          fontSize="sm"
          labelFontSize="sm"
          labelColor="gray.600"
          itemValueProps={{
            fontWeight: 'semibold',
            color: 'gray.900',
            justifyContent: 'flex-end',
            css: { fontVariantNumeric: 'tabular-nums' },
          }}
        />
      ))}
    </DataListRoot>
  );
}
